import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Cpu, Database, BarChart3, GitBranch, Workflow, Lock } from 'lucide-react';

const features = [
  {
    icon: Cpu,
    label: 'Automation',
    title: 'Intelligent Workflow Automation',
    subtitle: 'Operational Efficiency',
    desc: 'Proprietary workflow automation tools that eliminate manual bottlenecks in financial operations, reducing error rates and accelerating close cycles across the organization.',
    points: ['Manual bottleneck elimination', 'Error rate reduction', 'Close cycle acceleration', 'Repeatable process design'],
  },
  {
    icon: Database,
    label: 'Data Infrastructure',
    title: 'Financial Data Architecture',
    subtitle: 'Visibility & Structure',
    desc: 'Financial data architecture designed for real-time reporting, comprehensive audit trails, and cross-system visibility. Built to support both operational needs and capital events.',
    points: ['Real-time data access', 'Comprehensive audit trails', 'Cross-system integration', 'Scalable architecture'],
  },
  {
    icon: BarChart3,
    label: 'Reporting Engines',
    title: 'Custom Reporting Frameworks',
    subtitle: 'Actionable Intelligence',
    desc: 'Custom reporting frameworks that transform raw financial data into actionable management intelligence and stakeholder-ready packages for every audience.',
    points: ['Management intelligence', 'Stakeholder-specific reports', 'Automated distribution', 'KPI monitoring'],
  },
  {
    icon: GitBranch,
    label: 'Process Intelligence',
    title: 'Financial Process Intelligence',
    subtitle: 'Operational Design',
    desc: 'Mapping, optimizing, and automating complex financial workflows for maximum operational efficiency and compliance assurance. Every process designed with intent.',
    points: ['Process mapping & analysis', 'Workflow optimization', 'Compliance automation', 'Efficiency benchmarking'],
  },
  {
    icon: Workflow,
    label: 'Compliance Systems',
    title: 'Technology-Driven Compliance',
    subtitle: 'Regulatory Technology',
    desc: 'Technology-driven compliance management for payroll, reporting, and regulatory requirements across all jurisdictions — reducing risk while increasing speed.',
    points: ['Regulatory monitoring', 'Automated compliance checks', 'Multi-jurisdiction support', 'Compliance documentation'],
  },
  {
    icon: Lock,
    label: 'Control Environments',
    title: 'Institutional Control Environments',
    subtitle: 'Controls Architecture',
    desc: 'Building the internal controls and systems architecture that lenders, auditors, and institutional investors expect — the foundation of financial credibility.',
    points: ['Control environment design', 'Segregation of duties', 'Audit trail maintenance', 'Institutional standards compliance'],
  },
];

export default function Innovation() {
  const [activeTab, setActiveTab] = useState(0);
  const feat = features[activeTab];
  const FeatIcon = feat.icon;

  return (
    <div>
      {/* ── Hero ─────────────────────────────────── */}
      <section className="grid-bg pt-32 pb-14" data-testid="innovation-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-4">Innovation Ecosystem</p>
            <h1 className="text-5xl sm:text-6xl font-bold text-[#1A2234] leading-[1.05] tracking-tight mb-5 max-w-3xl">
              Technology-Enabled<br /><span className="text-[#0ABFBC]">Financial Infrastructure</span>
            </h1>
            <p className="text-lg text-[#475569] max-w-2xl leading-relaxed">
              Boston Federal is building the next generation of financial control tools — combining traditional accounting discipline with intelligent systems, automation, and proprietary technology.
            </p>
          </motion.div>
        </div>
      </section>

      {/* ── ScapeSource Intro ─────────────────────── */}
      <section className="bg-white py-16" data-testid="scapesource-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <motion.div initial={{ opacity: 0, x: -24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-3">Part of the Boston Federal Ecosystem</p>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#1A2234] leading-tight tracking-tight mb-6">Introducing ScapeSource</h2>
              <p className="text-[#475569] leading-relaxed mb-4">
                ScapeSource is the technology and innovation arm of the Boston Federal ecosystem — a platform dedicated to building proprietary financial control tools, intelligent automation systems, and next-generation reporting infrastructure.
              </p>
              <p className="text-[#475569] leading-relaxed mb-4">
                While Boston Federal handles the execution, ScapeSource powers the intelligence behind it — creating systems that make financial operations more automated, more visible, and more powerful.
              </p>
              <p className="text-[#475569] leading-relaxed mb-8">
                ScapeSource is not a separate company — it is Boston Federal's innovation engine, operating within and alongside our core financial services.
              </p>
              <Link to="/contact" data-testid="innovation-learn-more" className="bg-[#0ABFBC] text-white px-6 py-3 rounded-md font-semibold hover:bg-[#09A09D] transition-colors inline-flex items-center gap-2">
                Learn More <ArrowRight size={17} />
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 24 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <div className="space-y-4">
                <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-5">ScapeSource Builds</p>
                {['Proprietary financial control tools', 'Workflow automation systems', 'Intelligent reporting frameworks', 'Compliance technology infrastructure', 'Data architecture & visibility layers', 'Future financial control products'].map(item => (
                  <div key={item} className="flex items-start gap-3">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#0ABFBC] flex-shrink-0 mt-2" />
                    <span className="text-[#475569] text-sm">{item}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Technology Tab Section ────────────────── */}
      <section className="bg-[#F8FAFC] pb-20" data-testid="innovation-tabs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="pt-14 mb-8">
            <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.25em] mb-2">What ScapeSource Builds</p>
            <h2 className="text-3xl font-bold text-[#1A2234]">Technology for Financial Control</h2>
          </div>

          {/* Tab Bar */}
          <div className="border-b border-slate-200">
            <div className="flex overflow-x-auto no-scrollbar">
              {features.map((f, i) => (
                <button
                  key={f.label}
                  onClick={() => setActiveTab(i)}
                  data-testid={`innovation-tab-${i}`}
                  className={`px-5 py-4 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors duration-200 ${
                    i === activeTab ? 'text-[#0ABFBC] border-[#0ABFBC]' : 'text-[#475569] border-transparent hover:text-[#1A2234]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }}
              className="py-14 grid grid-cols-1 lg:grid-cols-5 gap-12 items-center"
            >
              <div className="lg:col-span-3">
                <div className="w-12 h-12 rounded-xl bg-[#0ABFBC]/10 flex items-center justify-center mb-5">
                  <FeatIcon size={22} strokeWidth={1.5} className="text-[#0ABFBC]" />
                </div>
                <p className="text-[#0ABFBC] text-[10px] font-bold uppercase tracking-[0.2em] mb-2">{feat.subtitle}</p>
                <h3 className="text-3xl font-bold text-[#1A2234] mb-4 leading-tight">{feat.title}</h3>
                <p className="text-[#475569] leading-relaxed mb-7">{feat.desc}</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                  {feat.points.map(p => (
                    <div key={p} className="flex items-start gap-3">
                      <div className="w-1.5 h-1.5 rounded-full bg-[#0ABFBC] flex-shrink-0 mt-[7px]" />
                      <span className="text-[#475569] text-sm">{p}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="lg:col-span-2 flex justify-center">
                <div className="w-52 h-52 rounded-full bg-[#0ABFBC]/5 flex items-center justify-center">
                  <div className="w-36 h-36 rounded-full bg-[#0ABFBC]/8 flex items-center justify-center">
                    <div className="w-20 h-20 rounded-full bg-[#0ABFBC]/12 flex items-center justify-center">
                      <FeatIcon size={40} strokeWidth={0.9} className="text-[#0ABFBC]/60" />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* ── Dark CTA ──────────────────────────────── */}
      <section className="dark-grid-bg py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mb-5">
              Technology-Enabled<br /><span className="text-[#0ABFBC]">Financial Control</span>
            </h2>
            <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto">The future of financial control is intelligent, automated, and built for scale. Boston Federal is building it.</p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" data-testid="innovation-cta-contact" className="bg-[#0ABFBC] text-white px-8 py-3.5 rounded-md font-semibold hover:bg-[#09A09D] transition-colors inline-flex items-center gap-2">
                Get in Touch <ArrowRight size={17} />
              </Link>
              <Link to="/capabilities" data-testid="innovation-cta-capabilities" className="border-2 border-white/40 text-white px-8 py-3.5 rounded-md font-semibold hover:bg-white hover:text-[#0B1120] transition-all inline-flex items-center gap-2">
                View Capabilities <ArrowRight size={17} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
